import { useMemo, useState } from "react";
import { Search, Download, LogOut, Mail, Phone, Inbox, NotebookPen, Trash2, CalendarClock, Send, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import StatusSelect, { STATUSES, statusClass } from "./StatusSelect";

const IST = { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" };
const fmt = (iso) => new Date(iso).toLocaleString("en-IN", IST);
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

const toCsv = (rows) => {
  const cols = ["created_at", "status", "next_action_at", "name", "email", "phone", "company", "category", "message", "notes"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

const download = (rows) => {
  const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `leads-${new Date().toISOString().slice(0, 10)}.csv` });
  a.click();
  URL.revokeObjectURL(url);
};

const selectCls = "h-10 rounded-md border border-navy/15 bg-white px-3 text-sm text-navy";

const Toolbar = ({ query, setQuery, category, setCategory, status, setStatus, categories, rows, onLogout, user }) => (
  <header className="flex flex-wrap items-end gap-4 justify-between">
    <div>
      <p className="text-xs uppercase tracking-[0.3em] font-semibold text-navy/60">The Formulation House</p>
      <h1 data-testid="admin-leads-title" className="font-display text-navy text-4xl mt-2">Leads <span className="text-navy/40 text-2xl">({rows.length})</span></h1>
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/50" />
        <Input data-testid="admin-leads-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, company…" className="pl-9 w-64 bg-white border-navy/15" />
      </div>
      <select data-testid="admin-leads-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
        <option value="">All statuses</option>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select data-testid="admin-leads-category" value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls}>
        <option value="">All categories</option>
        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <button data-testid="admin-leads-export" onClick={() => download(rows)} className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-4 py-2 text-sm text-navy hover:bg-navy hover:text-ivory transition-colors duration-300">
        <Download size={15} strokeWidth={1.5} /> CSV
      </button>
      <button data-testid="admin-logout-btn" onClick={onLogout} title={user?.email} className="inline-flex items-center gap-2 rounded-full bg-navy text-ivory px-4 py-2 text-sm hover:opacity-90 transition-opacity">
        <LogOut size={15} strokeWidth={1.5} /> Sign out
      </button>
    </div>
  </header>
);

const Pipeline = ({ leads, status, setStatus, onDigest, digesting }) => {
  const dueCount = leads.filter((l) => l.next_action_at && l.next_action_at <= today() && l.status !== "Won").length;
  return (
    <div className="flex flex-wrap items-stretch justify-between gap-4">
      <div data-testid="admin-pipeline" className="flex flex-wrap gap-3">
        {STATUSES.map((s) => {
          const n = leads.filter((l) => (l.status || "New") === s).length;
          const active = status === s;
          return (
            <button key={s} data-testid={`admin-pipeline-${s.toLowerCase()}`} onClick={() => setStatus(active ? "" : s)}
              className={`rounded-xl border px-5 py-3 text-left transition-all duration-300 hover:-translate-y-0.5 ${active ? "ring-2 ring-navy/60" : ""} ${statusClass(s)}`}>
              <p className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-80">{s}</p>
              <p className="font-display text-2xl leading-none mt-1">{n}</p>
            </button>
          );
        })}
      </div>
      <button data-testid="admin-send-digest-btn" onClick={onDigest} disabled={digesting}
        className="group inline-flex items-center gap-3 self-center rounded-xl border border-navy/20 bg-cream px-5 py-3 text-left text-navy transition-all duration-300 hover:-translate-y-0.5 hover:bg-navy hover:text-ivory disabled:opacity-60">
        {digesting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
        <span>
          <span className="block text-sm font-semibold">Send today&apos;s follow-ups now</span>
          <span data-testid="admin-digest-due-count" className="block text-[11px] opacity-70">{dueCount ? `${dueCount} lead${dueCount > 1 ? "s" : ""} due or overdue` : "Nothing due right now"}</span>
        </span>
      </button>
    </div>
  );
};

const DUE_STYLES = {
  overdue: { cls: "bg-red-100 text-red-800 border-red-300", prefix: "Overdue · " },
  today: { cls: "bg-amber-100 text-amber-900 border-amber-300", prefix: "Today · " },
  upcoming: { cls: "bg-navy/5 text-navy border-navy/15", prefix: "" },
};

const dueState = (date) => {
  if (date < today()) return "overdue";
  if (date === today()) return "today";
  return "upcoming";
};

const NextAction = ({ date }) => {
  if (!date) return null;
  const { cls, prefix } = DUE_STYLES[dueState(date)];
  return (
    <span data-testid="lead-next-action-badge" className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      <CalendarClock size={11} strokeWidth={1.5} /> {prefix}{fmtDate(date)}
    </span>
  );
};

const LeadRow = ({ lead, i, saving, onStatus, onNotes, onDelete }) => (
  <tr data-testid={`admin-lead-row-${i}`} className="border-t border-navy/10 align-top hover:bg-powder/40 transition-colors">
    <td className="px-4 py-4 whitespace-nowrap text-xs text-charcoal/70">{fmt(lead.created_at)}</td>
    <td className="px-4 py-4">
      <p className="font-semibold text-navy">{lead.name}</p>
      {lead.company && <p className="text-xs text-charcoal/60">{lead.company}</p>}
    </td>
    <td className="px-4 py-4 text-sm space-y-1">
      <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-navy hover:underline"><Mail size={13} strokeWidth={1.5} />{lead.email}</a>
      <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-navy hover:underline"><Phone size={13} strokeWidth={1.5} />{lead.phone}</a>
    </td>
    <td className="px-4 py-4"><span className="inline-block rounded-full bg-navy/10 text-navy text-xs px-2.5 py-1">{lead.category}</span></td>
    <td className="px-4 py-4"><StatusSelect lead={lead} index={i} saving={saving} onChange={onStatus} /></td>
    <td className="px-4 py-4 text-sm text-charcoal/80 whitespace-pre-wrap max-w-sm">{lead.message || <span className="text-charcoal/40">—</span>}</td>
    <td className="px-4 py-4 text-sm min-w-[12rem] max-w-xs">
      <NextAction date={lead.next_action_at} />
      {lead.notes && <p data-testid={`admin-lead-notes-${i}`} className={`text-charcoal/75 whitespace-pre-wrap line-clamp-3 ${lead.next_action_at ? "mt-1.5" : ""}`}>{lead.notes}</p>}
      {!lead.notes && !lead.next_action_at && <span className="text-charcoal/40">—</span>}
    </td>
    <td className="px-2 py-4 whitespace-nowrap">
      <button data-testid={`admin-lead-notes-btn-${i}`} onClick={() => onNotes(lead)} title="Notes & next action" className="p-2 rounded-full text-navy/70 hover:text-navy hover:bg-navy/10 transition-colors"><NotebookPen size={16} strokeWidth={1.5} /></button>
      <button data-testid={`admin-lead-delete-btn-${i}`} onClick={() => onDelete(lead)} title="Delete lead" className="p-2 rounded-full text-red-700/70 hover:text-red-700 hover:bg-red-100 transition-colors"><Trash2 size={16} strokeWidth={1.5} /></button>
    </td>
  </tr>
);

export default function LeadsTable({ leads, user, onLogout, onStatus, savingId, onNotes, onDelete, onDigest, digesting }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const categories = useMemo(() => [...new Set(leads.map((l) => l.category))].sort(), [leads]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => (!category || l.category === category) && (!status || (l.status || "New") === status) &&
      (!q || [l.name, l.email, l.company, l.phone, l.message, l.notes].some((v) => (v || "").toLowerCase().includes(q))));
  }, [leads, query, category, status]);

  return (
    <div data-testid="admin-leads-page" className="min-h-screen bg-ivory px-6 lg:px-10 py-12">
      <div className="max-w-7xl mx-auto space-y-8">
        <Toolbar {...{ query, setQuery, category, setCategory, status, setStatus, categories, rows, onLogout, user }} />
        <Pipeline leads={leads} status={status} setStatus={setStatus} onDigest={onDigest} digesting={digesting} />
        <div className="overflow-x-auto rounded-2xl border border-navy/10 bg-cream">
          <table className="w-full text-left">
            <thead className="text-[11px] uppercase tracking-[0.2em] text-navy/60">
              <tr>{["Received", "Name", "Contact", "Category", "Status", "Message", "Follow-up", "Actions"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap"><span className={h === "Actions" ? "sr-only" : ""}>{h}</span></th>)}</tr>
            </thead>
            <tbody>{rows.map((l, i) => <LeadRow key={l.id} lead={l} i={i} saving={savingId === l.id} onStatus={onStatus} onNotes={onNotes} onDelete={onDelete} />)}</tbody>
          </table>
          {rows.length === 0 && (
            <div data-testid="admin-leads-empty" className="grid place-items-center py-20 text-charcoal/50 gap-2">
              <Inbox size={28} strokeWidth={1.2} /><p className="text-sm">No leads match.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
